import { ArrowRight, ChevronRight, ShieldCheck, Truck } from "lucide-react";

import type { CarrierProviderAddOnService } from "@/api/carrier";
import { LoadingRows } from "@/components/data-table";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface PickupServiceCatalogueProps {
  rows: CarrierProviderAddOnService[];
  initialLoading: boolean;
  hasFilters: boolean;
  onView: (service: CarrierProviderAddOnService) => void;
}

export function PickupServiceCatalogue({
  rows,
  initialLoading,
  hasFilters,
  onView,
}: PickupServiceCatalogueProps) {
  return (
    <>
      <div className="md:hidden">
        {initialLoading ? (
          <div role="status" className="p-8 text-center text-sm">
            Loading pickup services…
          </div>
        ) : rows.length ? (
          <ul aria-label="Pickup service summaries" className="divide-y">
            {rows.map((service) => (
              <li key={service.id}>
                <Button
                  type="button"
                  variant="ghost"
                  aria-label={`View ${service.name}`}
                  className="h-auto min-h-16 w-full justify-start gap-3 rounded-none px-4 py-3 text-left whitespace-normal hover:bg-muted/50"
                  onClick={() => onView(service)}
                >
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium">
                        {service.name}
                      </span>
                      <StatusBadge
                        status={service.active ? "ACTIVE" : "INACTIVE"}
                      />
                    </span>
                    <span className="mt-1 block truncate text-xs text-muted-foreground">
                      {service.provider.name} · {labelAccessSummary(service)}
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <PickupServicesEmptyState filtered={hasFilters} />
        )}
      </div>

      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Service</TableHead>
              <TableHead>Provider</TableHead>
              <TableHead className="text-right">DIM (cm³)</TableHead>
              <TableHead className="text-right">DIM (in³)</TableHead>
              <TableHead>Label carriers</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {initialLoading ? (
              <LoadingRows columns={7} rows={3} />
            ) : rows.length ? (
              rows.map((service) => (
                <TableRow key={service.id}>
                  <TableCell>
                    <div className="font-medium">{service.name}</div>
                    <code className="text-xs text-muted-foreground">
                      {service.code}
                    </code>
                  </TableCell>
                  <TableCell>{service.provider.name}</TableCell>
                  <TableCell className="text-right text-sm text-muted-foreground">
                    {formatValue(service.pickupDetails?.dimFactorCm)}
                  </TableCell>
                  <TableCell className="text-right text-sm text-muted-foreground">
                    {formatValue(service.pickupDetails?.dimFactorInch)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {labelAccessSummary(service)}
                  </TableCell>
                  <TableCell>
                    <StatusBadge
                      status={service.active ? "ACTIVE" : "INACTIVE"}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={`View details for ${service.name}`}
                        onClick={() => onView(service)}
                      >
                        Configure <ArrowRight className="ml-1 h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="p-0">
                  <PickupServicesEmptyState filtered={hasFilters} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

export function PickupServicesPermissionState() {
  return (
    <div role="alert" className="p-8 text-center text-sm text-muted-foreground">
      <ShieldCheck className="mx-auto mb-2 h-9 w-9 text-destructive opacity-70" />
      You do not have permission to view pickup services.
    </div>
  );
}

function PickupServicesEmptyState({ filtered }: { filtered: boolean }) {
  return (
    <div className="p-8 text-center text-sm text-muted-foreground">
      <Truck className="mx-auto mb-2 h-9 w-9 opacity-50" />
      {filtered
        ? "No pickup services match these filters."
        : "No pickup services yet. EZX must grant pickup-provider access before you can add one."}
    </div>
  );
}

function formatValue(value: unknown): string {
  return value === null || value === undefined || value === ""
    ? "—"
    : String(value);
}

function labelAccessSummary(service: CarrierProviderAddOnService): string {
  const enabled = service.labelCarrierAccess.filter(
    (access) => access.enabled
  ).length;
  const total = service.labelCarrierAccess.length;
  if (total === 0) return "No label carriers";
  return `${enabled}/${total} enabled`;
}
